import axios from "axios";
import { supabase } from "../supabaseClient";

const BASE = `${process.env.REACT_APP_PAYMENT_LINK_URL}api/cp-sourcing`;

// Must match backend services/cpSourcing/cpSourcingHelpers.js OUTCOMES.
export const CP_SOURCING_OUTCOMES = [
  "Connected",
  "Not reachable",
  "Busy",
  "Not interested",
  "Will resume business",
  "Stopped business",
];

// Visibility scoping (mirrors CallLogs):
//   scope="all"        → every assigned CP (Head / "All" mode)
//   userIds=[a,b,c]    → team-wise (self + descendants)
//   userId=x           → single user (own CPs)
export const getDormantCps = async ({ userId, userIds, scope, tier, search, limit = 20, offset = 0 }) => {
  const params = { limit, offset };
  if (scope === "all") params.scope = "all";
  else if (Array.isArray(userIds) && userIds.length) params.userIds = userIds.join(",");
  else params.userId = userId;
  if (tier) params.tier = tier;
  if (search) params.search = search;
  const res = await axios.get(`${BASE}/dormant-cps`, { params });
  return res.data; // {success, data:{tiers, total, rows}}
};

export const getCpActivity = async (cpId) => {
  const res = await axios.get(`${BASE}/cps/${cpId}/activity`);
  return res.data; // {success, data:[...]}
};

// Last N site visits of a CP (SM name, date, project short code) for the card collapse.
export const getCpSiteVisits = async (cpId, limit = 5) => {
  const res = await axios.get(`${BASE}/cps/${cpId}/site-visits`, { params: { limit } });
  return res.data; // {success, data:[{siteVisitId, created_at, smName, projectShortCode}]}
};

export const addCpActivity = async (cpId, { userId, outcome, remark }) => {
  const res = await axios.post(`${BASE}/cps/${cpId}/activity`, { userId, outcome, remark });
  return res.data; // {success, data:<created row>}
};

// Store a CP site visit. Photo is sent as a base64 data URL; the backend uploads
// it to the "cpsitevisit" storage bucket and stores the key in photoUrl.
export const addCpSiteVisit = async (
  cpId,
  {
    userId,
    projectId,
    mode,
    contactNumber,
    remark,
    dateTime,
    startTime,
    endTime,
    photoBase64,
  }
) => {
  const res = await axios.post(`${BASE}/cps/${cpId}/site-visits`, {
    userId,
    projectId,
    mode,
    contactNumber,
    remark,
    dateTime,
    startTime,
    endTime,
    photoBase64,
  });
  return res.data; // {success, data:<created row>}
};

// Hierarchy-scoped list of CP site visits for the approval view.
export const getCpSiteVisitsList = async ({ userId, userIds, scope, search, status, limit = 50, offset = 0 }) => {
  const params = { limit, offset };
  if (scope === "all") params.scope = "all";
  else if (Array.isArray(userIds) && userIds.length) params.userIds = userIds.join(",");
  else params.userId = userId;
  if (search) params.search = search;
  if (status) params.status = status;
  const res = await axios.get(`${BASE}/get-all-cp-site-visits`, { params });
  return res.data; // {success, data:[...], total}
};

// Approve/reject a CP site visit. status = 'approved' | 'rejected'. The backend
// enforces that userId is the CP's approver (parent of the CP's sourcing user).
export const verifyCpSiteVisit = async (cpId, siteVisitId, { userId, status, comment }) => {
  const res = await axios.post(`${BASE}/cps/${cpId}/site-visits/${siteVisitId}/verify`, {
    userId,
    status,
    comment,
  });
  return res.data; // {success, data:<updated row>}
};

// Contact group for a sourcing member: same contract as the SM
// createGroupAndUpload, but uploads the member's linked CPs.
export const createCpGroupAndUpload = async (payload) => {
  const res = await axios.post(`${BASE}/create-group-and-upload`, payload);
  return res.data; // {success, smartfloGroupId, uploadedCount, skippedNoNumber, message}
};

// On CP approval: push the CP's contact into its sourcing user's SmartFlo
// group (mirror of the claim-lead upload). Skips silently server-side when
// the CP has no sourcing user / group / number.
export const uploadCpToContactGroup = async (cpId) => {
  const res = await axios.post(`${BASE}/upload-cp-to-contactgroup`, { cpId });
  return res.data; // {success, uploaded, skipped?}
};

// Projects for the CP Site Visit dropdown. Same scoping the rest of the app uses
// for project pickers (isVJ), narrowed to published projects only.
export const getPublishedProjects = async () => {
  const { data, error } = await supabase
    .from("Projects")
    .select("projectId, projectName")
    .eq("isVJ", true)
    .eq("isPublished", true)
    .order("projectName", { ascending: true });

  if (error) throw error;
  return data || [];
};
